import React from 'react'
import { RouteComponentProps } from '@reach/router'
import useImportDetails from './hooks/useImportDetails'
import useImportFailed, { INeighborhoodImportFailedWithId } from './hooks/useImportFailed'
import Appbar from '../../Appbar'
import { omit } from 'lodash'
import {
  IconButton,
  Flex,
  PageContent,
  IGridTableColumn,
  Skeleton,
  NewGridTable,
  Popover,
} from '@ui'
import { INeighborhoodImportFailed, INeighborhoodImportVersion } from '@api'
import { Tag } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { DateUtil } from '@utils'

export default function ImportDetails({ id, navigate }: { id: string } & RouteComponentProps) {
  const { details, onNextPage, status, pageInfo, createdAt, importedBy } = useImportDetails(id)
  const { failedImportNeighborhoods } = useImportFailed(id)

  const NA = 'N/A'
  const columns: IGridTableColumn<INeighborhoodImportVersion | INeighborhoodImportFailedWithId>[] =
    [
      {
        label: 'Status',
        Cell: ({ skeleton, cell }) => {
          if (skeleton || !cell) {
            return <Skeleton />
          }
          if ('errors' in cell?.data) {
            return (
              <Popover
                interactionKind={'hover'}
                content={<FailedReasons failedNeighborhood={cell.data ?? {}} />}
              >
                <Tag intent="danger" minimal round>
                  Failed
                </Tag>
              </Popover>
            )
          }
          return (
            <Tag intent="success" minimal round>
              Created
            </Tag>
          )
        },
      },
      {
        label: 'Name',
        Cell: ({ skeleton, cell }) => {
          if (skeleton) return <Skeleton />
          if (!cell) return null
          return (
            <div className="flex flex-col">
              <div>{cell?.data.employee_neighborhood?.employee.name || NA}</div>
            </div>
          )
        },
      },
      {
        label: 'Email',
        Cell: ({ skeleton, cell }) => {
          if (skeleton) return <Skeleton />
          if (!cell) return null
          return (
            <div className="flex flex-col">
              <div>{cell?.data.employee_neighborhood?.employee.email || NA}</div>
            </div>
          )
        },
      },
      {
        label: 'Employee Department',
        Cell: ({ skeleton, cell }) => {
          if (skeleton) return <Skeleton />
          if (!cell) return null
          return (
            <div className="flex flex-col">
              <div>{cell?.data.employee_neighborhood?.employee?.department.name || NA}</div>
            </div>
          )
        },
      },
      {
        label: 'Neighborhood',
        Cell: ({ skeleton, cell }) => {
          if (skeleton) return <Skeleton />
          if (!cell) return null
          return (
            <div className="flex flex-col">
              <div>{cell?.data.employee_neighborhood?.neighborhood?.name || NA}</div>
            </div>
          )
        },
      },
      {
        label: 'Location',
        Cell: ({ skeleton, cell }) => {
          if (skeleton) return <Skeleton />
          if (!cell) return null
          const neighborhood = cell?.data.employee_neighborhood?.neighborhood
          return (
            <div className="flex flex-col">
              <div>
                {(neighborhood &&
                  `${neighborhood.floor.name}, ${neighborhood.floor.building.name}\n${neighborhood.floor.building.city}`) ||
                  NA}
              </div>
            </div>
          )
        },
      },
    ]

  return (
    <>
      <Appbar
        title={
          <Flex align="center" justify="center">
            <span style={{ marginRight: 8 }}>
              <Tooltip2 content="Back to Import History">
                <IconButton
                  onClick={() => navigate?.('/neighborhoods/history')}
                  iconName="chevron-left"
                  minimal
                />
              </Tooltip2>
            </span>
            <Flex direction="column">
              <span style={{ fontSize: 14 }}>Import Details</span>
              <span
                style={{
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  color: 'var(--grey1)',
                }}
              >
                {createdAt &&
                  `${DateUtil(createdAt).format('MMM d, yyyy @h:mma')} by ${importedBy}`}
              </span>
            </Flex>
          </Flex>
        }
      />
      <PageContent>
        <NewGridTable<INeighborhoodImportVersion | INeighborhoodImportFailedWithId>
          status={status}
          // there is some difficult validation stuff going on here that
          // needs to be resolved before you can remove the ignore
          /* eslint-disable @typescript-eslint/ban-ts-comment */
          // @ts-ignore
          data={[...failedImportNeighborhoods, ...details]}
          columns={columns}
          page={{
            hasNextPage: pageInfo?.hasNextPage || false,
            endCursor: pageInfo?.endCursor || '',
            onNextPage,
          }}
        />
      </PageContent>
    </>
  )
}

function FailedReasons({ failedNeighborhood }: { failedNeighborhood: INeighborhoodImportFailed }) {
  if (
    failedNeighborhood.errors === null ||
    Object.values(omit(failedNeighborhood.errors, '__typename')).every((value) => value === null)
  ) {
    return (
      <div
        style={{
          maxWidth: 350,
          padding: 20,
          zIndex: 100,
        }}
      >
        Unknown Error
      </div>
    )
  }

  return (
    <Flex direction="column" style={{ padding: 20 }}>
      {Object.entries(failedNeighborhood.errors).map(([key, value]) => {
        if (!value || key === '__typename') return null
        return <span key={key}>{value}</span>
      })}
    </Flex>
  )
}
